import {
  LogLevel,
  type Configuration,
  type PopupRequest,
  type RedirectRequest,
} from '@azure/msal-browser';

/**
 * Entra External ID (CIAM) configuration — see docs/identity.md.
 * Every value is supplied at build time so each environment points at its own
 * app registration and API, exactly like VITE_API_BASE_URL (M0-16).
 */
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const authority = import.meta.env.VITE_ENTRA_AUTHORITY ?? '';
const apiScope = import.meta.env.VITE_API_SCOPE ?? '';

/** True when the app has enough configuration to attempt sign-in. */
export const isAuthConfigured = Boolean(clientId && authority && apiScope);

/** CIAM authorities are not on login.microsoftonline.com, so MSAL needs it allow-listed. */
function knownAuthorityHost(value: string): string[] {
  try {
    return [new URL(value).hostname];
  } catch {
    return [];
  }
}

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    knownAuthorities: knownAuthorityHost(authority),
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
  },
  cache: {
    // sessionStorage, never localStorage: tokens die with the tab rather than
    // persisting on a shared machine — an office PC is a realistic setting here.
    // (MSAL v5 removed storeAuthStateInCookie and navigateToLoginRequestUrl.)
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii && level === LogLevel.Error) {
          console.error('[msal]', message);
        }
      },
    },
  },
};

/** Scopes requested at sign-in. */
export const loginRequest: RedirectRequest & PopupRequest = {
  scopes: [apiScope],
};

/** Scopes for calling the API; acquired silently once signed in. */
export const apiTokenRequest = {
  scopes: [apiScope],
};
